import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Upload, ArrowRight } from "lucide-react";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const MEXAL_FIELDS = ["codice", "descrizione", "giacenza", "prezzo"];

export default function AdminMexalCsv() {
    const [session, setSession] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [mapping, setMapping] = useState({});
    const [saveAs, setSaveAs] = useState("");
    const [confirming, setConfirming] = useState(false);

    const upload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true); setSession(null); setMapping({});
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/admin/mexal/csv-preview", fd, {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 120000,
            });
            setSession(data);
            // Auto-guess mapping via header names (case-insensitive contains)
            const guess = {};
            const cols = data.columns || [];
            MEXAL_FIELDS.forEach((k) => {
                const found = cols.find((c) => c.toLowerCase().includes(k));
                if (found) guess[k] = found;
            });
            setMapping(guess);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore upload");
        } finally {
            setUploading(false);
            e.target.value = "";
        }
    };

    const applySavedMapping = (m) => {
        setMapping(m.mapping || {});
        toast.success(`Mapping "${m.name}" applicato`);
    };

    const confirm = async () => {
        if (!session) return;
        if (!mapping.codice || !mapping.descrizione) {
            toast.error("Mappa almeno codice e descrizione");
            return;
        }
        setConfirming(true);
        try {
            const { data } = await api.post(`/admin/mexal/csv-confirm/${session.session_id}`,
                { mapping, save_as: saveAs || null }, { timeout: 300000 });
            toast.success(`${data.new_created} nuovi vini · ${data.updated_stock} giacenze aggiornate`);
            setSession(null); setMapping({}); setSaveAs("");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore conferma");
        } finally { setConfirming(false); }
    };

    return (
        <div data-testid="admin-mexal-csv-page">
            <h1 className="text-3xl mb-2">Import CSV Mexal</h1>
            <p className="text-sm text-[hsl(var(--muted-foreground))] mb-6 max-w-2xl">
                Carica un export CSV dal gestionale Mexal. Il formato reale delle colonne può
                variare: mappa manualmente le colonne del tuo file ai campi standard
                (codice, descrizione, giacenza, prezzo) e opzionalmente salva il mapping
                per gli import futuri.
            </p>

            <div className="bg-white border-2 border-dashed border-[hsl(var(--border))] rounded-sm p-8 text-center mb-8">
                <input id="mexal-csv-input" type="file" accept=".csv,text/csv" onChange={upload} className="hidden" data-testid="mexal-csv-input" />
                <label htmlFor="mexal-csv-input" className="cursor-pointer inline-flex flex-col items-center gap-3">
                    <Upload className="w-10 h-10 text-[hsl(var(--muted-foreground))]" strokeWidth={1.2} />
                    <div className="text-base">{uploading ? "Analisi CSV in corso…" : "Trascina qui o clicca per caricare CSV"}</div>
                </label>
            </div>

            {session && (
                <div data-testid="mexal-csv-preview" className="space-y-6">
                    <div className="bg-white border border-[hsl(var(--border))] rounded-sm p-5">
                        <h2 className="text-xl mb-4">Mapping colonne · {session.count} righe · delimiter <code>{session.delimiter || ";"}</code></h2>

                        {session.saved_mappings?.length > 0 && (
                            <div className="mb-4">
                                <Label className="co-caption mb-2 block">Riapplica mapping salvato</Label>
                                <div className="flex gap-2 flex-wrap">
                                    {session.saved_mappings.map((m) => (
                                        <button
                                            key={m.name} type="button"
                                            onClick={() => applySavedMapping(m)}
                                            className="co-btn-ghost text-xs"
                                            data-testid={`csv-load-mapping-${m.name}`}
                                        >
                                            {m.name}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {MEXAL_FIELDS.map((k) => (
                                <div key={k}>
                                    <Label className="co-caption mb-1.5 block">
                                        {k} {(k === "codice" || k === "descrizione") && <span className="text-[hsl(var(--destructive))]">*</span>}
                                    </Label>
                                    <Select value={mapping[k] || "__none"} onValueChange={(v) => setMapping({ ...mapping, [k]: v === "__none" ? "" : v })}>
                                        <SelectTrigger data-testid={`csv-map-${k}`} className="bg-white"><SelectValue placeholder="—" /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="__none">—</SelectItem>
                                            {(session.columns || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                            ))}
                        </div>

                        <div className="mt-6 pt-4 border-t border-[hsl(var(--border))]">
                            <Label className="co-caption mb-1.5 block">Salva mapping come (opzionale)</Label>
                            <Input placeholder="Es. mexal-standard" value={saveAs} onChange={(e) => setSaveAs(e.target.value)} data-testid="csv-save-as" className="bg-white w-full sm:w-64" />
                        </div>
                    </div>

                    {session.sample?.length > 0 && (
                        <div className="bg-white border border-[hsl(var(--border))] rounded-sm p-5">
                            <h3 className="text-base mb-3">Anteprima prime {session.sample.length} righe</h3>
                            <div className="overflow-x-auto text-xs">
                                <table className="w-full">
                                    <thead>
                                        <tr>{(session.columns || []).map((c) => <th key={c} className="text-left pb-2 px-2 co-caption">{c}</th>)}</tr>
                                    </thead>
                                    <tbody>
                                        {session.sample.map((row, i) => (
                                            <tr key={i} className="border-t border-[hsl(var(--border))]">
                                                {(session.columns || []).map((c) => <td key={c} className="py-1 px-2 max-w-[200px] truncate">{String(row[c] ?? "")}</td>)}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    <button onClick={confirm} disabled={confirming} className="co-btn-primary" data-testid="csv-confirm-btn">
                        <ArrowRight className="w-4 h-4" />
                        {confirming ? "Import in corso (può richiedere qualche minuto)…" : "Conferma import"}
                    </button>
                </div>
            )}
        </div>
    );
}
